import { emptySurveyQuestionData } from "utils/consts";
import { swapPositions } from "utils/swap-positions";

/**
 * Add a new question with a position one past the current last question,
 * falling back to the server-side length when the form is empty.
 *
 * @param {Array} questions - current form questions array
 * @param {Function} appendCallback - useFieldArray append callback
 * @param {number} surveyQuestionsLength - length of server-side questions
 * @returns {Object} the question that was appended (for testing)
 */
export function addQuestion(questions, appendCallback, surveyQuestionsLength) {
    const safeLength =
        typeof surveyQuestionsLength === "number" ? surveyQuestionsLength : 0;
    const position =
        questions && questions.length > 0
            ? (questions[questions.length - 1]?.position || 0) + 1
            : safeLength + 1;
    const newQuestion = { ...emptySurveyQuestionData, position };
    appendCallback(newQuestion);
    return newQuestion;
}

/**
 * Clone a question for insertion after the original. Caller inserts the
 * returned `clonedQuestion` via the field array insert at insertIndex.
 *
 * @param {Object} question - the question to clone
 * @param {number} index - current index of the question in the questions field array
 * @returns {{ clonedQuestion: Object, insertIndex: number }}
 */
export function cloneQuestion(question, index) {
    const clonedQuestion = structuredClone(question);
    delete clonedQuestion.id;
    delete clonedQuestion.nanoid;
    clonedQuestion.position = (question?.position ?? 0) + 1;

    if (Array.isArray(clonedQuestion.preset_answers)) {
        clonedQuestion.preset_answers.forEach((pa) => {
            delete pa.id;
            delete pa.nanoid;
            delete pa.question_id;
        });
    }

    return {
        clonedQuestion,
        insertIndex: index + 1,
    };
}

/**
 * Swap a question one position up (lower index), updating position fields.
 *
 * @param {Function} swap - useFieldArray swap callback
 * @param {Object} values - current form values
 * @param {number} index - index of the question to move
 * @param {Function} setFieldValue - path-writing setValue for a field
 */
export function moveQuestionUp(swap, values, index, setFieldValue) {
    swapPositions({
        readPosition: (i) => values.questions[i].position,
        writePosition: (i, position) =>
            setFieldValue(`questions.${i}.position`, position),
        swap,
        index,
        delta: -1,
    });
}

/**
 * Swap a question one position down (higher index) and update positions.
 */
export function moveQuestionDown(swap, values, index, setFieldValue) {
    swapPositions({
        readPosition: (i) => values.questions[i].position,
        writePosition: (i, position) =>
            setFieldValue(`questions.${i}.position`, position),
        swap,
        index,
        delta: 1,
    });
}

/**
 * Reindex question positions to be contiguous starting from 1.
 *
 * @param {Array} questions - form questions array after removal
 * @returns {Array} reindexed questions (new array)
 */
export function reindexAfterDelete(questions) {
    if (!questions || questions.length === 0) return questions;
    return questions.map((q, index) => ({ ...q, position: index + 1 }));
}
